import { useEffect } from 'react';
import {
  useQuery,
  useInfiniteQuery,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import {
  fetchUnifiedCodebaseManagement,
  UnifiedRepository,
} from '@/apis/unifiedCodebaseManagement';
import useUserContextStore from '@/contexts/UserContext';

// Short fingerprint of the OAuth access token. Keeps full secrets out of
// query keys (which are visible in React Query devtools) while still letting
// TanStack treat a rotated token as a fresh cache entry.
const tokenFingerprint = (token?: string | null): string => {
  if (!token) return '';
  return token.length <= 8 ? token : `${token.length}:${token.slice(-8)}`;
};

// Query keys for consistent caching
export const unifiedCodebaseQueryKeys = {
  all: ['unifiedCodebaseManagement'],
  byOrg: (
    orgUUID: string,
    userUUID?: string,
    limit?: number,
    accessToken?: string
  ) => [
    'unifiedCodebaseManagement',
    'org',
    orgUUID,
    userUUID,
    limit,
    tokenFingerprint(accessToken),
  ],
  paginated: (
    orgUUID: string,
    userUUID?: string,
    search?: string,
    prBotOnly?: boolean,
    prBotOrHasDocs?: boolean,
    sortBy?: string,
    order?: string,
    bitbucketProjectKeys?: string[],
    gitlabGroupPath?: string,
    accessToken?: string
  ) => [
    'unifiedCodebaseManagement',
    'paginated',
    orgUUID,
    userUUID,
    search,
    prBotOnly,
    prBotOrHasDocs,
    sortBy,
    order,
    (bitbucketProjectKeys ?? []).join('\x1e'),
    gitlabGroupPath ?? '',
    tokenFingerprint(accessToken),
  ],
  page: (
    orgUUID: string,
    userUUID: string,
    pageIndex: number,
    pageSize: number,
    search?: string,
    prBotOnly?: boolean,
    prBotOrHasDocs?: boolean,
    bitbucketProjectKeys?: string[],
    gitlabGroupPath?: string,
    accessToken?: string
  ) => [
    'unifiedCodebaseManagement',
    'page',
    orgUUID,
    userUUID,
    pageIndex,
    pageSize,
    search ?? '',
    prBotOnly ?? false,
    prBotOrHasDocs ?? false,
    (bitbucketProjectKeys ?? []).join('\x1e'),
    gitlabGroupPath ?? '',
    tokenFingerprint(accessToken),
  ],
};

// Hook for fetching unified codebase management data
// Returns first page of results - use usePaginatedRepositories for infinite scroll
// Exposes total_count, has_more, and apiError from the endpoint.
export const useUnifiedCodebaseManagement = (
  orgUUID: string,
  userUUID: string = '',
  options?: {
    enabled?: boolean;
    staleTime?: number;
    refetchOnWindowFocus?: boolean;
    limit?: number; // Optional limit, defaults to 100
  }
) => {
  const limit = options?.limit ?? 50; // Matches backend default and industry standards
  const accessToken = useUserContextStore((s) => s.user?.accessToken);

  const query = useQuery({
    queryKey: unifiedCodebaseQueryKeys.byOrg(
      orgUUID,
      userUUID,
      limit,
      accessToken
    ),
    queryFn: async () => {
      const result = await fetchUnifiedCodebaseManagement(
        orgUUID,
        userUUID,
        limit,
        0
      );
      return result;
    },
    enabled: Boolean(orgUUID) && options?.enabled !== false,
    staleTime: options?.staleTime ?? 5 * 60 * 1000, // 5 minutes default
    refetchOnWindowFocus: options?.refetchOnWindowFocus ?? false,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  const data = (query.data?.repositories ?? []) as UnifiedRepository[];
  return {
    ...query,
    data,
    total_count: query.data?.total_count ?? 0,
    has_more: query.data?.has_more ?? false,
    /** Org-level agent-insights default for repos with no explicit override. */
    agent_insights_org_default:
      query.data?.agent_insights_org_default ?? true,
    apiError: query.data?.error,
  };
};

// Hook for paginated repositories with infinite scroll support
// Exposes totalCount and apiError from the first page response.
export const usePaginatedRepositories = (
  orgUUID: string,
  userUUID: string = '',
  options?: {
    enabled?: boolean;
    search?: string;
    prBotOnly?: boolean;
    pageSize?: number;
    sortBy?: string;
    order?: string;
    bitbucketProjectKeys?: string[];
    gitlabGroupPath?: string;
    staleTime?: number;
  }
) => {
  const pageSize = options?.pageSize ?? 50;
  const search = options?.search ?? '';
  const prBotOnly = options?.prBotOnly ?? false;
  const sortBy = options?.sortBy ?? 'name';
  const order = options?.order ?? 'asc';
  const bitbucketProjectKeys = options?.bitbucketProjectKeys;
  const gitlabGroupPath = options?.gitlabGroupPath;
  const accessToken = useUserContextStore((s) => s.user?.accessToken);

  type PageParam =
    | { kind: 'page'; value: number } // GitHub/GitLab: 1-indexed page
    | { kind: 'cursor'; value: string | null }; // Bitbucket: cursor URL (null = first page)

  // Widened via annotation + cast at the use sites below: TS flow analysis
  // otherwise narrows this const and drops the cursor arm, collapsing the
  // useInfiniteQuery generic inference for every consumer.
  const initialPageParam = { kind: 'page', value: 1 } as PageParam;

  const query = useInfiniteQuery({
    queryKey: unifiedCodebaseQueryKeys.paginated(
      orgUUID,
      userUUID,
      search,
      prBotOnly,
      false,
      sortBy,
      order,
      bitbucketProjectKeys,
      gitlabGroupPath,
      accessToken
    ),
    queryFn: ({ pageParam = initialPageParam }) =>
      fetchUnifiedCodebaseManagement(
        orgUUID,
        userUUID,
        pageSize,
        0,
        search,
        prBotOnly,
        false,
        sortBy,
        order,
        {
          bitbucketProjectKeys,
          gitlabGroupPath,
          providerPageSize: pageSize,
          ...(pageParam.kind === 'page'
            ? { providerPage: pageParam.value }
            : { providerCursor: pageParam.value }),
        }
      ),
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.has_more) return undefined;
      // If backend gave a cursor, we must use it (Bitbucket).
      if (typeof lastPage.provider_next_cursor === 'string') {
        return {
          kind: 'cursor',
          value: lastPage.provider_next_cursor,
        } as PageParam;
      }
      // Next provider page number (GitHub/GitLab). Checked before the
      // null-cursor bailout below because GitHub/GitLab always send
      // provider_next_cursor: null and page via provider_next_page — bailing
      // on the null cursor would stop pagination after the first page.
      if (typeof lastPage.provider_next_page === 'number') {
        return {
          kind: 'page',
          value: lastPage.provider_next_page,
        } as PageParam;
      }
      // Cursor-based provider (Bitbucket) with no further cursor: exhausted.
      if (lastPage.provider_next_cursor === null) {
        return undefined;
      }
      // Conservative default: increment by page count (page 1,2,3...)
      return { kind: 'page', value: allPages.length + 1 } as PageParam;
    },
    enabled: Boolean(orgUUID) && options?.enabled !== false,
    staleTime: options?.staleTime ?? 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    retry: 2,
    initialPageParam,
  });

  const firstPage = query.data?.pages?.[0];
  return {
    ...query,
    totalCount: firstPage?.total_count ?? 0,
    apiError: firstPage?.error,
  };
};

/** Options for server-side paginated repository fetching */
export interface UseRepositoriesPageOptions {
  enabled?: boolean;
  pageIndex: number;
  pageSize: number;
  search?: string;
  prBotOnly?: boolean;
  prBotOrHasDocs?: boolean;
  staleTime?: number;
  bitbucketProjectKeys?: string[];
  gitlabGroupPath?: string;
}

/**
 * Hook for one page of repositories with total_count and has_more from the API.
 * Use this when the UI needs server-side pagination (e.g. Security Repository Status table).
 * Prefetches the next page when has_more is true so "Next" feels instant.
 */
export const useRepositoriesPage = (
  orgUUID: string,
  userUUID: string,
  options: UseRepositoriesPageOptions
) => {
  const {
    pageIndex,
    pageSize,
    search = '',
    prBotOnly = false,
    prBotOrHasDocs = false,
    enabled = true,
    staleTime = 5 * 60 * 1000,
    bitbucketProjectKeys,
    gitlabGroupPath,
  } = options;

  const queryClient = useQueryClient();
  const accessToken = useUserContextStore((s) => s.user?.accessToken);

  // Bitbucket uses cursor-based pagination — pull the previous page's
  // `provider_next_cursor` out of the query cache so we can forward it.
  const prevPageData =
    pageIndex > 0
      ? queryClient.getQueryData<{ provider_next_cursor?: string | null }>(
          unifiedCodebaseQueryKeys.page(
            orgUUID,
            userUUID,
            pageIndex - 1,
            pageSize,
            search.trim() || undefined,
            prBotOnly,
            prBotOrHasDocs,
            bitbucketProjectKeys,
            gitlabGroupPath,
            accessToken
          )
        )
      : undefined;
  const prevCursor =
    typeof prevPageData?.provider_next_cursor === 'string'
      ? prevPageData.provider_next_cursor
      : undefined;

  const query = useQuery({
    queryKey: unifiedCodebaseQueryKeys.page(
      orgUUID,
      userUUID,
      pageIndex,
      pageSize,
      search.trim() || undefined,
      prBotOnly,
      prBotOrHasDocs,
      bitbucketProjectKeys,
      gitlabGroupPath,
      accessToken
    ),
    queryFn: async () => {
      const result = await fetchUnifiedCodebaseManagement(
        orgUUID,
        userUUID,
        pageSize,
        0,
        search.trim(),
        prBotOnly,
        prBotOrHasDocs,
        'name',
        'asc',
        {
          bitbucketProjectKeys,
          gitlabGroupPath,
          providerPageSize: pageSize,
          ...(prevCursor
            ? { providerCursor: prevCursor }
            : { providerPage: pageIndex + 1 }),
        }
      );
      return result;
    },
    enabled: Boolean(orgUUID) && enabled,
    staleTime,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    retry: 2,
    retryDelay: (i) => Math.min(1000 * 2 ** i, 30000),
  });

  const repositories = (query.data?.repositories ?? []) as UnifiedRepository[];
  const total_count = query.data?.total_count ?? 0;
  const has_more = query.data?.has_more ?? false;
  const apiError = query.data?.error;

  // Prefetch next page when there is one, so pagination feels instant.
  // Skip while we're showing placeholder data (e.g. mid-filter-switch) — the
  // cursor on `query.data` belongs to the previous filter and would poison
  // the new filter's cache under the new query key.
  useEffect(() => {
    if (!has_more || !orgUUID || !query.data) return;
    if (query.isPlaceholderData) return;
    const nextIndex = pageIndex + 1;
    const nextSearch = search.trim() || undefined;
    const nextCursor =
      typeof query.data?.provider_next_cursor === 'string'
        ? query.data.provider_next_cursor
        : undefined;
    queryClient.prefetchQuery({
      queryKey: unifiedCodebaseQueryKeys.page(
        orgUUID,
        userUUID,
        nextIndex,
        pageSize,
        nextSearch,
        prBotOnly,
        prBotOrHasDocs,
        bitbucketProjectKeys,
        gitlabGroupPath,
        accessToken
      ),
      queryFn: () =>
        fetchUnifiedCodebaseManagement(
          orgUUID,
          userUUID,
          pageSize,
          0,
          search.trim(),
          prBotOnly,
          prBotOrHasDocs,
          'name',
          'asc',
          {
            bitbucketProjectKeys,
            gitlabGroupPath,
            providerPageSize: pageSize,
            ...(nextCursor
              ? { providerCursor: nextCursor }
              : { providerPage: nextIndex + 1 }),
          }
        ),
      staleTime,
    });
  }, [
    queryClient,
    orgUUID,
    userUUID,
    pageIndex,
    pageSize,
    search,
    prBotOnly,
    prBotOrHasDocs,
    bitbucketProjectKeys,
    gitlabGroupPath,
    has_more,
    query.data,
    query.isPlaceholderData,
    staleTime,
    accessToken,
  ]);

  return {
    repositories,
    total_count,
    has_more,
    /** Org-level agent-insights default for repos with no explicit override. */
    agent_insights_org_default:
      query.data?.agent_insights_org_default ?? true,
    /** Backend "Error" field from the API response */
    apiError,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    isFetching: query.isFetching,
    isSuccess: query.isSuccess,
  };
};

// Hook for getting repositories with loading and error states
// Exposes total_count, has_more, apiError from the fetchUnifiedCodebaseManagement endpoint.
export const useRepositories = (
  orgUUID: string,
  userUUID: string = '',
  options?: {
    enabled?: boolean;
    staleTime?: number;
    refetchOnWindowFocus?: boolean;
    limit?: number;
  }
) => {
  const query = useUnifiedCodebaseManagement(orgUUID, userUUID, options);

  const repositories = Array.isArray(query.data) ? query.data : [];

  return {
    repositories,
    total_count: query.total_count ?? 0,
    has_more: query.has_more ?? false,
    agent_insights_org_default: query.agent_insights_org_default ?? true,
    apiError: query.apiError,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    isFetching: query.isFetching,
    isSuccess: query.isSuccess,
  };
};

// Hook for getting a specific repository by name
export const useRepository = (
  orgUUID: string,
  repoName: string,
  userUUID: string = '',
  options?: {
    enabled?: boolean;
    staleTime?: number;
    refetchOnWindowFocus?: boolean;
  }
) => {
  const query = useUnifiedCodebaseManagement(orgUUID, userUUID, options);

  const repositories = Array.isArray(query.data) ? query.data : [];
  const repository = repositories.find((repo) => repo.name === repoName);

  return {
    repository,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    isFetching: query.isFetching,
    isSuccess: query.isSuccess,
  };
};

// Hook for getting repositories with datasources only
export const useRepositoriesWithDatasources = (
  orgUUID: string,
  userUUID: string = '',
  options?: {
    enabled?: boolean;
    staleTime?: number;
    refetchOnWindowFocus?: boolean;
  }
) => {
  const query = useUnifiedCodebaseManagement(orgUUID, userUUID, options);

  // Filter repos that have repoUUID (indicates they're ingested/have datasource)
  const repositories = Array.isArray(query.data) ? query.data : [];
  const repositoriesWithDatasources = repositories.filter(
    (repo) => repo.repoUUID
  );

  return {
    repositories: repositoriesWithDatasources,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    isFetching: query.isFetching,
    isSuccess: query.isSuccess,
  };
};

// Hook for getting repositories with PR bot enabled
export const useRepositoriesWithPRBot = (
  orgUUID: string,
  userUUID: string = '',
  options?: {
    enabled?: boolean;
    staleTime?: number;
    refetchOnWindowFocus?: boolean;
  }
) => {
  const query = useUnifiedCodebaseManagement(orgUUID, userUUID, options);

  const repositories = Array.isArray(query.data) ? query.data : [];
  const repositoriesWithPRBot = repositories.filter((repo) => repo.hasPRBot);

  return {
    repositories: repositoriesWithPRBot,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    isFetching: query.isFetching,
    isSuccess: query.isSuccess,
  };
};

// Hook for getting repository statistics
export const useRepositoryStats = (
  orgUUID: string,
  userUUID: string = '',
  options?: {
    enabled?: boolean;
    staleTime?: number;
    refetchOnWindowFocus?: boolean;
  }
) => {
  const query = useUnifiedCodebaseManagement(orgUUID, userUUID, options);

  const repositories = Array.isArray(query.data) ? query.data : [];
  const stats =
    repositories.length > 0
      ? {
          total: repositories.length,
          withPRBot: repositories.filter((repo) => repo.hasPRBot).length,
          withDependencies: repositories.filter(
            (repo) => repo.dependencies.length > 0
          ).length,
        }
      : null;

  return {
    stats,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    isFetching: query.isFetching,
    isSuccess: query.isSuccess,
  };
};
